import { LitElement, html, css } from 'lit';

import './DashboardLoadingOverlay.js';
import './DashboardModalWindow.js';
import './Tooltip.js';
import '../FetchMixin.js';
import { FetchMixin } from '../FetchMixin.js';
import './MessageBox.js';
import alertStore from '../AlertStore.js';
import { setAlert } from '../AlertSlice.js';
import dashboardSelectedAppStore from './DashboardSelectedAppStore.js';
import { selectApp } from './DashboardSelectedAppSlice.js';
import userSettingStore from '../UserSettingStore.js';
import { loadUserSettingData } from '../UserSettingSlice.js';
import './DashboardAppList.js';

class DashboardAppContent extends FetchMixin(LitElement) {

  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .section {
      background: transparent;
      margin-bottom: 24px;
      opacity: 1;
      transition: opacity 0.4s ease, transform 0.4s ease;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px; /* 警告文との余白調整のために縮小 */
      padding: 0 8px;
    }

    .section-header h1 {
      font-size: 1.5rem;
      font-weight: 800;
      color: #111827;
      margin: 0;
      display: flex;
      align-items: center;
    }

    .workbench_caution {
      margin: 0 8px 24px 8px; /* 左右のpaddingと下余白を設定 */
      font-size: 13px;
      color: #6b7280;
    }

    .header-actions {
      display: flex;
      gap: 12px;
      align-items: center;
    }

    .btn-create {
      background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 6px rgba(79, 70, 229, 0.2);
      transition: transform 0.2s, box-shadow 0.2s;
    }

    .btn-create:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(79, 70, 229, 0.3);
    }

    .btn-archive-link {
      background: white;
      color: #4b5563;
      border: 1px solid #d1d5db;
      padding: 10px 16px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: background-color 0.2s;
    }

    .btn-archive-link:hover {
      background: #f3f4f6;
      color: #111827;
    }

    .form-group-item {
      display: flex;
      flex-direction: column;
      margin-bottom: 16px;
    }

    .form-group-item label {
      font-weight: 600;
      color: #374151;
      font-size: 14px;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    input[type="text"] {
      padding: 10px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    input[type="text"]:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }

    .required { color: #ef4444; }
    .error-message { color: #ef4444; font-size: 12px; visibility: hidden; margin-top: 4px; min-height: 16px; }

    /* モーダル用ボタン */
    .confirm-ok-button { background-color: #ef4444; color: white; border: none; padding: 10px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; }
    .confirm-cancel-button { background-color: #f3f4f6; color: #374151; border: none; padding: 10px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; }
    .question { font-size: 40px; display: flex; margin: 0 0 16px 0; justify-content: center; color: #9ca3af; }

    @keyframes slideInLeft {
      from { transform: translateX(-20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    .slide-in-left { animation: slideInLeft 0.4s cubic-bezier(0.16, 1, 0.3, 1); }

    @keyframes highlight {
      0% { background-color: transparent; }
      30% { background-color: #dbeafe; }
      100% { background-color: transparent; }
    }
    .highlight-animation { animation: highlight 1.5s ease; }

    @media (max-width: 768px) {
      .section-header { flex-direction: column; align-items: flex-start; gap: 16px; }
      .header-actions { width: 100%; justify-content: space-between; }
    }
  `;

  static get properties() {
    return {
      identKey: { type: String },
      apps: { type: Array },
      loading: { type: Boolean },
      dailyReportLastTime: { type: Date },
      serverTime: { type: Date },
      _isDataLoaded: { type: Boolean, state: true },
    };
  }

  constructor() {
    super();
    this.identKey = null;
    this.apps = [];
    this.loading = false;
    this.dailyReportLastTime = null;
    this.serverTime = null;
    this._isDataLoaded = false;
    this.addEventListener('modal-submit', this.handleModalSubmit.bind(this));
    userSettingStore.subscribe(() => this.requestUpdate());
  }

  connectedCallback() {
    super.connectedCallback();
    // DOMに追加された瞬間に無条件でAPIを叩くのをやめ、親(DashboardMainContent)からの指示（または初回データ流し込み）に任せます。
  }

  firstUpdated() {
    requestAnimationFrame(async () => {
      const fontAwasomeStyle = document.querySelector("link[rel='stylesheet'][data-name='fontawesomev6']");
      if (fontAwasomeStyle) {
        const fontAwasomeStyleClone = fontAwasomeStyle.cloneNode(true);
        fontAwasomeStyleClone.removeAttribute('disabled');
        this.shadowRoot.insertBefore(fontAwasomeStyleClone, this.shadowRoot.firstChild);
      }
    })
  }

  injectAnimationStyles(targetElement) {
    if (!targetElement || !targetElement.shadowRoot) return;
    if (targetElement.shadowRoot.querySelector('#injected-animations')) return;

    const style = document.createElement('style');
    style.id = 'injected-animations';
    style.textContent = `
      @keyframes slideInLeft { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      .slide-in-left { animation: slideInLeft 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
      @keyframes highlight { 0% { background-color: transparent; } 30% { background-color: #dbeafe; } 100% { background-color: transparent; } }
      .highlight-animation { animation: highlight 1.5s ease; }
    `;
    targetElement.shadowRoot.appendChild(style);
  }

  updated(changedProperties) {
    if (changedProperties.has('identKey') && this.identKey) {
      this.dispatchEvent(new CustomEvent('changeIdentKey', { detail: { identKey: this.identKey }, bubbles: true, composed: true }));
    }
    if (changedProperties.has('apps')) {
      const oldApplications = changedProperties.get('apps') || [];
      const appListEl = this.shadowRoot.querySelector('dashboard-app-list');
      if (appListEl) this.injectAnimationStyles(appListEl);
      const rows = appListEl ? appListEl.shadowRoot.querySelectorAll('.app-card') : [];

      if (this.apps.length < oldApplications.length) return;

      rows.forEach((row, index) => {
        const isNewRow = index >= oldApplications.length;
        if (isNewRow) this.applyRowAnimation(row, 'slide-in-left');
        const oldApp = oldApplications[index];
        const newApp = this.apps[index];
        if (oldApp && newApp && JSON.stringify(oldApp) !== JSON.stringify(newApp)) {
          this.applyRowAnimation(row, 'highlight-animation');
        }
      });
    }
  }

  applyRowAnimation(element, animationClass) {
    element.classList.add(animationClass);
    element.addEventListener('animationend', () => {
      element.classList.remove(animationClass);
    }, { once: true });
  }

  // 親から呼ばれる、またはアーカイブなどから戻った際に呼ばれるメソッド
  async showApplicationList() {
    if (this._isDataLoaded) {
      // 既に親からデータをもらっている場合は再描画のみ行い通信はしない
      this.requestUpdate();
      return;
    }
    this.loading = true;
    try {
      await this.fetchApplicationList();
    } catch (error) { console.error('Error fetching application list:', error); }
    finally { this.loading = false; }
  }

  async fetchApplicationList() {
    const appListEndpoint = process.env.APPLICATION_LIST_ENDPOINT;
    const json = await this.fetchWithAuth(appListEndpoint, { method: 'GET' });
    this.identKey = json.details.identKey;
    this.apps = json.details.applications;
    this._isDataLoaded = true;

    this.dailyReportLastTime = new Date(json.details.dailyReportLastTime);
    this.serverTime = new Date(json.details.serverTime);
    await this.sendDailyReport();
    return json;
  }

  isSameDate(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
  }

  async sendDailyReport() {
    if (this.serverTime.getHours() >= 4 && !this.isSameDate(this.dailyReportLastTime, this.serverTime)) {
      try {
        const response = await this.fetchWithAuth(process.env.APPLICATION_DAILY_REPORT_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
        if (response.code !== 200) throw new Error(response.message);
        this.dispatchEvent(new CustomEvent('updatePwappyPoint', { bubbles: true, composed: true }));
      } catch (error) { console.error('デイリーレポート送信エラー:', error); }
    }
  }

  async handleModalSubmit(event) {
    if (event.detail.dialogName === 'appModal') {
      let appErrorFlag = false;
      let inputAppName = this.shadowRoot.getElementById('input-app-name')
      let errAppName = this.shadowRoot.getElementById('error-app-name')
      errAppName.style.visibility = 'hidden';
      let errorMessagesName = [];
      if (!inputAppName.checkValidity()) {
        appErrorFlag = true;
        if (inputAppName.validity.valueMissing) errorMessagesName.push(`必須項目です`);
        if (inputAppName.validity.tooLong) errorMessagesName.push(`最大文字数を超えています`);
        if (errorMessagesName.length > 0) { errAppName.textContent = errorMessagesName.join(' '); errAppName.style.visibility = 'visible'; }
      }

      let inputAppKey = this.shadowRoot.getElementById('input-app-key')
      let errAppKey = this.shadowRoot.getElementById('error-app-key')
      errAppKey.style.visibility = 'hidden';
      let errorMessagesKey = [];
      if (!inputAppKey.checkValidity()) {
        appErrorFlag = true;
        if (inputAppKey.validity.valueMissing) errorMessagesKey.push(`必須項目です`);
        if (inputAppKey.validity.patternMismatch) errorMessagesKey.push(`英小文字、数字、ハイフン、アンダーバーのみ入力可能です`);
        if (inputAppKey.validity.tooLong) errorMessagesKey.push(`最大文字数を超えています`);
        if (errorMessagesKey.length > 0) { errAppKey.textContent = errorMessagesKey.join(' '); errAppKey.style.visibility = 'visible'; }
      }

      if (!appErrorFlag) {
        let options = { method: 'POST', body: JSON.stringify({ appName: inputAppName.value, appKey: inputAppKey.value }) }
        this.loading = true;
        try {
          let ret = await this.fetchWithAuth(process.env.APPLICATION_ENDPOINT, options);
          if (ret.code !== 200) {
            if (ret.status === "app_create_user_space_error") alertStore.dispatch(setAlert("割り当てられたスペースを超えているので作成できませんでした"));
            else if (ret.status === "app_create_duplicate_key_error") {
              errAppKey.textContent = `アプリケーションキーが重複しています`; errAppKey.style.visibility = 'visible'; return;
            } else alertStore.dispatch(setAlert("エラーが発生しました"));
            throw new Error(JSON.stringify(ret));
          }
          this.shadowRoot.querySelector('#appModal').hide();
          await this.fetchApplicationList();

          // 作成完了後、自動的に詳細画面へ遷移する
          dashboardSelectedAppStore.dispatch(selectApp(inputAppKey.value));

        } catch (e) { console.error(e); } finally { this.loading = false; }
      }
    }
  }

  async addNewAppDialog() {
    this.clearAppDialog();
    let appModal = this.shadowRoot.querySelector('#appModal');
    await appModal.show();
    this.shadowRoot.getElementById('input-app-name').focus();
  }

  clearAppDialog() {
    let inputs = this.shadowRoot.querySelectorAll('.modal-input');
    inputs.forEach(input => input.value = '');
    let errorMessages = this.shadowRoot.querySelectorAll('.error-message');
    errorMessages.forEach(message => message.style.visibility = 'hidden');
  }

  keyDownEvent(event) {
    if (event.key === 'Enter') {
      if (event.target.id === 'input-app-name') {
        this.shadowRoot.getElementById('input-app-key').focus();
      } else if (event.target.id === 'input-app-key') {
        this.handleModalSubmit(new CustomEvent('modal-submit', { bubbles: true, composed: true, detail: { dialogName: 'appModal' } }));
      }
    }
  }

  goToArchive() {
    this.dispatchEvent(new CustomEvent('switch-view', { detail: { view: 'archive' }, bubbles: true, composed: true }));
  }

  async archiveApp(appKey) {
    this.loading = true;
    try {
      await this.fetchWithAuth(process.env.APPLICATION_ARCHIVE_ENDPOINT, { method: 'POST', body: JSON.stringify({ appKey: appKey }) });
      alertStore.dispatch(setAlert(`アプリをアーカイブに退避しました`));
      await this.fetchApplicationList();
      this.dispatchEvent(new CustomEvent('updatePwappyPoint', { bubbles: true, composed: true }));
    } catch (e) {
      console.error(e);
      alertStore.dispatch(setAlert('アーカイブ中にエラーが発生しました'));
    } finally {
      this.loading = false;
    }
  }

  async showArchiveConfirm(appKey) {
    const messageBox = this.shadowRoot.querySelector('#archive-confirm-shortcut');
    const ret = await messageBox.show();
    if (ret) {
      this.archiveApp(appKey);
    }
    messageBox.hide();
  }

  _handleAppAction(e) {
    const { action, appKey } = e.detail;
    if (action === 'select') {
      dashboardSelectedAppStore.dispatch(selectApp(appKey));
    } else if (action === 'archive') {
      this.showArchiveConfirm(appKey);
    }
  }

  render() {
    return html`
      ${this.loading ? html`<dashboard-loading-overlay></dashboard-loading-overlay>` : ''}  

      <dashboard-modal-window id="appModal">
        <span slot="header-title">新規アプリケーションの作成</span>
        <div slot="content">
          <div class="form-group">
            <div class="form-group-item">
              <label for="input-app-name">アプリケーション名<span class="required">*</span><tooltip-element text="一覧に表示されたときに識別しやすい名前を付けてください\n\n最大30文字"></tooltip-element></label>
              <input type="text" id="input-app-name" class="modal-input" name="app-name" required maxlength="30" @keydown="${this.keyDownEvent}" placeholder="例: Todoアプリ">
              <div class="error-message" id="error-app-name"></div>
            </div>
            <div class="form-group-item">
              <label for="input-app-key">アプリケーションキー<span class="required">*</span><tooltip-element text="アプリケーションを一意に識別するための文字列です。URLに使用されます。\n\n最大30文字"></tooltip-element></label>
              <input type="text" id="input-app-key" class="modal-input" name="app-key" required maxlength="30" pattern="^[a-z0-9\\-_]+$" @keydown="${this.keyDownEvent}" placeholder="例: todo-app">
              <div class="error-message" id="error-app-key"></div>
            </div>
          </div>
        </div>
        <span slot="submit-button-text">作成</span> 
      </dashboard-modal-window>

      <message-box id="archive-confirm-shortcut">
        <div slot="message-slot">
            <p class="question"><i class="fa-solid fa-circle-question"></i></p>
            <p style="text-align: center; font-weight: bold;">アーカイブに退避しますか？<br><span style="font-size:0.8em; color:#6b7280; font-weight:normal;">※１ＰＰ使用します</span></p>
        </div>
        <button slot="ok-slot" class="confirm-ok-button" style="background:#d97706;">アーカイブ</button>
        <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
      </message-box>

      <div class="section app-management">
        <div class="section-header">
          <h1><i class="fa-solid fa-laptop-code" style="color: #3b82f6; margin-right: 8px;"></i> ワークベンチ</h1>
          <div class="header-actions">
            <button class="btn-archive-link" @click="${this.goToArchive}"><i class="fa-solid fa-box-archive"></i> アーカイブ</button>
            <button class="btn-create" title="アプリケーションを作成" @click="${this.addNewAppDialog}"><i class="fa-solid fa-plus"></i> 新規作成</button>
          </div>
        </div>
        
        <p class="workbench_caution"><i class="fa-solid fa-circle-info" style="color:#3b82f6;"></i> ワークベンチにあるアプリケーションは1つあたり1日で２ＰＰを使用します。</p>

        <dashboard-app-list 
          mode="workbench" 
          .apps=${this.apps} 
          @app-action=${this._handleAppAction}
          @trigger-create-app=${this.addNewAppDialog}
        ></dashboard-app-list>
      </div>
    `;
  }
}

customElements.define('dashboard-app-content', DashboardAppContent);